import { useEffect, useRef, useCallback, useState } from "react";
import Page from "./Page";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import LoadingScreen from "../components/loading/LoadingScreen";
import Unsubscribed from "../components/account/Unsubscribed";
import Subscribed from "../components/account/Subscribed";
import { signOut } from "../helpers/auth";
import { dispatch } from "../redux/store";
import { setUser, setUserSubscription } from "../redux/actions/user";
import { useNavigate } from "react-router-dom";
import Button from "../components/input/button";
import Friend from "../components/account/Friend";
import adminUsers from "../constants/adminUsers";
import useSubscriptionAPIClient from "../api/subscription/hooks/useSubscriptionAPIClient";
import useRevenueCat from "../hooks/subscription/useRevenueCat";
import PreferencesDialog from "../components/dialogs/PreferencesDialog";

// Polling interval in milliseconds (2 seconds)
// Now that pub/sub is fixed, updates should come through much faster
const SUBSCRIPTION_POLL_INTERVAL = 2000;

const AccountPage = () => {
  const { activeUser, subscription } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { getSubscriptionStatus } = useSubscriptionAPIClient();
  const { getSubscriptionInfo, isNative } = useRevenueCat();
  const pollIntervalRef = useRef(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Store the latest function refs to avoid resetting the interval
  const getSubscriptionStatusRef = useRef(getSubscriptionStatus);
  const getSubscriptionInfoRef = useRef(getSubscriptionInfo);
  const isNativeRef = useRef(isNative);
  const activeUserRef = useRef(activeUser);

  // Keep refs up to date
  useEffect(() => {
    getSubscriptionStatusRef.current = getSubscriptionStatus;
    getSubscriptionInfoRef.current = getSubscriptionInfo;
    isNativeRef.current = isNative;
    activeUserRef.current = activeUser;
  });

  const refreshSubscription = useCallback(async () => {
    if (!activeUserRef.current) return;

    try {
      // First, get the backend subscription status
      const backendSubscription = await getSubscriptionStatusRef.current();

      // On native platforms, also check RevenueCat directly
      // RevenueCat SDK knows about purchases immediately, even before webhooks
      if (isNativeRef.current) {
        const revenueCatInfo = await getSubscriptionInfoRef.current();

        if (revenueCatInfo?.isActive) {
          // RevenueCat shows active subscription
          // If backend still shows cancelling but RevenueCat says it will renew,
          // the user has resubscribed - update the status optimistically
          if (
            backendSubscription.status === "cancelling" &&
            revenueCatInfo.willRenew
          ) {
            dispatch(
              setUserSubscription({
                ...backendSubscription,
                status: "active",
                cancellingAt: undefined,
              }),
            );
            return;
          }
        }
      }

      dispatch(setUserSubscription(backendSubscription));
    } catch (error) {
      console.error("Failed to refresh subscription status:", error);
    }
  }, []);

  // Refresh subscription status when the page loads and poll periodically
  // This handles returning from Play Store/Stripe where the webhook may have
  // updated the subscription status, but the local state is stale
  useEffect(() => {
    // Initial fetch
    refreshSubscription();

    // Set up polling interval
    pollIntervalRef.current = setInterval(
      refreshSubscription,
      SUBSCRIPTION_POLL_INTERVAL,
    );

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [refreshSubscription]);

  const onLogoutButtonClicked = () => {
    signOut();
    dispatch(setUser(null));
    navigate("/");
  };

  const onAdminButtonClicked = () => {
    navigate("/admin");
  };

  if (!activeUser) {
    return <LoadingScreen />;
  }

  return (
    <Page className="account-page" pageKey="account">
      <div className="account-page_container">
        <div className="account-page_panel">
          <div className="account-page_user-info">
            <div>
              <Typography variant="label" className="account-page_info-label">
                Name
              </Typography>
              <Typography variant="p1" className="account-page_label-value">
                {activeUser.displayName}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="account-page_info-label">
                Email
              </Typography>
              <Typography variant="p1" className="account-page_label-value">
                {activeUser.email}
              </Typography>
            </div>

            {activeUser.userId && adminUsers.includes(activeUser.userId) && (
              <Box sx={{ mb: { xs: 1, sm: 2 }, mt: { xs: 2, sm: 3 } }}>
                <Button
                  text="⚙️ Admin"
                  color="primary"
                  size={{ xs: "large", sm: "huge" }}
                  onClick={onAdminButtonClicked}
                />
              </Box>
            )}

            <Box sx={{ mb: { xs: 1, sm: 2 }, mt: { xs: 1, sm: 1 } }}>
              <Button
                text="⚙️ Preferences"
                color="primary"
                size={{ xs: "large", sm: "huge" }}
                onClick={() => setPreferencesOpen(true)}
              />
            </Box>

            <Button
              text="Logout"
              color="red"
              size={{ xs: "large", sm: "huge" }}
              onClick={onLogoutButtonClicked}
            />
          </div>
          <div className="account-page_subscription-info">
            {subscription ? (
              <>
                {subscription.plan === "free" && <Unsubscribed />}
                {subscription.plan === "member" && <Subscribed />}
                {subscription.plan === "friend" && <Friend />}
              </>
            ) : (
              <CircularProgress size={40} />
            )}
          </div>
        </div>
      </div>

      <PreferencesDialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </Page>
  );
};

export default AccountPage;
